import { useEffect, useRef, useState } from "react";
import ImageFallback from "@/components/common/ImageFallback";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import { CT } from "./catalogueTheme";
import CUTOUT_STEMS from "@/data/cutoutIndex.json";

// Cutouts live in public/product-cutouts/<source-image-file-stem>.webp and
// are produced by scripts/generate_product_cutouts.py from the very same
// product photos uploaded in Admin. Keyed by the upload's unique file stem,
// so a product whose image is replaced in Admin simply has no cutout (and
// falls back to its original photo) until the script is re-run.
const CUTOUT_BASE = `${process.env.PUBLIC_URL || ""}/product-cutouts`;

// Stems that have a cutout on disk (src/data/cutoutIndex.json, regenerated before every start/build by
// scripts/build-cutout-index.js). Only these are ever requested — an image uploaded from Admin has no cutout, and
// asking the server for one would be a guaranteed 404.
const HAS_CUTOUT = new Set(CUTOUT_STEMS);

// Remember stems that have no cutout so a grid of them doesn't re-request
// (and re-404) on every mount/filter change.
const missingCutouts = new Set();

// Images imported from the manufacturer's catalogue (Admin-uploaded folders named
// "products-<category>") are already white-background product photos: they're shown
// whole (`object-contain`) with the white blended into the card, and never go through the
// dark-banner cutout lookup below.
export const isCataloguePhoto = (src) => /\/(uploads\/products-[^/]+|images\/products)\//.test(String(src || ""));

export function cutoutUrl(src) {
  if (!src || isCataloguePhoto(src)) return null;
  const file = String(src).split("?")[0].split("/").pop() || "";
  const stem = file.replace(/\.[^.]+$/, "");
  return stem && HAS_CUTOUT.has(stem) ? `${CUTOUT_BASE}/${stem}.webp` : null;
}

/**
 * The one place product photography is rendered on the catalogue pages.
 *
 * - Cutout available  → the product on a clean light backdrop, centred,
 *   `object-contain` (never stretched/cropped), with a soft silhouette
 *   shadow. This is the "catalogue photography" look.
 * - No cutout         → the original photo, cover-fitted so a wide banner
 *   still fills the frame rather than being letterboxed into a tiny strip.
 * - No image at all   → an explicit icon (never `<img src="">`, which would
 *   make the browser request the page's own HTML as an image).
 *
 * Fills its parent (`h-full w-full`): the parent owns the aspect ratio, so
 * every card in a grid gets an identical image frame. Zoom-on-hover is
 * driven by an ancestor `group` (see ProductCard) via `group-hover:`.
 */
export default function ProductImage({ src, alt = "", className = "", padding = "p-6", priority = false, zoom = true, bare = false }) {
  const cutout = cutoutUrl(src);
  const initialMode = () => (!src ? "none" : isCataloguePhoto(src) ? "catalogue" : cutout && !missingCutouts.has(cutout) ? "cutout" : "original");
  const [mode, setMode] = useState(initialMode);
  const [loaded, setLoaded] = useState(false);

  const root = useRef(null);
  const firstRun = useRef(true);
  // A different image (e.g. a filter re-mount reusing the component) resets the state machine —
  // but not on first mount: an already-cached image can fire `load` before this effect runs, and
  // resetting then would leave it hidden (opacity-0) for good.
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setLoaded(false);
    setMode(initialMode());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, cutout]);
  // If the <img> finished loading before React attached its handler (browser cache), reveal it.
  useEffect(() => {
    const el = root.current?.querySelector("img");
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [mode, src]);

  const frame = `relative h-full w-full overflow-hidden ${className}`;
  // `bare`: transparent backdrop, for callers that draw their own surface behind the photo (the hero).
  const backdrop = bare ? undefined : { background: `radial-gradient(ellipse at 50% 42%, ${CT.white} 0%, #F1F5F9 100%)` };

  if (mode === "none") {
    return (
      <div ref={root} className={frame}>
        <ImageFallback label={alt} className="h-full w-full" />
      </div>
    );
  }

  const zoomCls = zoom ? "transition-transform duration-500 ease-out group-hover:scale-[1.045]" : "";
  const fade = `transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`;

  if (mode === "catalogue") {
    return (
      <div ref={root} className={frame} style={backdrop}>
        {!loaded && <div className="absolute inset-0 animate-pulse bg-slate-100/70" aria-hidden="true" />}
        <div className={`absolute inset-0 ${padding} ${zoomCls}`}>
          <img
            src={toAbsoluteUrl(src)}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setMode("none")}
            className={`h-full w-full object-contain mix-blend-multiply ${fade}`}
          />
        </div>
      </div>
    );
  }

  if (mode === "cutout") {
    return (
      <div ref={root} className={frame} style={backdrop}>
        {!loaded && <div className="absolute inset-0 animate-pulse bg-slate-100/70" aria-hidden="true" />}
        <div className={`absolute inset-0 ${padding} ${zoomCls}`}>
          <img
            src={cutout}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => { missingCutouts.add(cutout); setMode("original"); setLoaded(false); }}
            className={`h-full w-full object-contain drop-shadow-[0_14px_14px_rgba(11,33,72,0.16)] ${fade}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={root} className={frame} style={backdrop}>
      {!loaded && <div className="absolute inset-0 animate-pulse bg-slate-100/70" aria-hidden="true" />}
      <img
        src={toAbsoluteUrl(src)}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setMode("none")}
        className={`absolute inset-0 h-full w-full object-cover ${zoomCls} ${fade}`}
      />
    </div>
  );
}
